import React, { Suspense, lazy, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Outlet, useLocation } from "react-router-dom";

import {
  Box,
  CssBaseline,
  Paper as MuiPaper,
  Container as MuiContainer,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { spacing } from "@mui/system";

import GlobalStyle from "../components/GlobalStyle";
import Navbar from "../components/navbar/Navbar";
import dashboardItems from "../components/sidebar/dashboardItems";
import Sidebar from "../components/sidebar/Sidebar";
import Footer from "../components/Footer";
import ImpersonationBanner from "../components/ImpersonationBanner";
import StalePermissionsBanner from "../components/StalePermissionsBanner";
import apiClient from "@utils/axiosInstance";

// LWI-699 — KBPanel pulls in TipTap (~250KB) transitively. Lazy-load it
// so non-KB routes don't pay the cost. Width is mirrored from KBPanel.jsx
// (keep in sync if changed there).
const KBPanel = lazy(() => import("../components/knowledgeBase/KBPanel"));
const KB_PANEL_WIDTH = 300; // LWI-749 — mirror of KBPanel.jsx, keep in sync

// LWI-720 — full + collapsed (icons-only) drawer widths.
const drawerWidth = 258;
const drawerWidthCollapsed = 64;
const SIDEBAR_COLLAPSED_KEY = "sidebarCollapsed";

const Root = styled.div`
  display: flex;
  min-height: 100vh;
`;

const Drawer = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${(props) => props.$width}px;
    flex-shrink: 0;
    transition: width 0.2s;
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: calc(100% - ${(props) => props.$drawerWidth}px${(props) => props.$kbOpen ? ` - ${KB_PANEL_WIDTH}px` : ""});
    transition: max-width 0.2s;
  }
`;

const Paper = styled(MuiPaper)(spacing);

const Container = styled(MuiContainer)`
  height: 100%;
`;

const MainContent = styled(Paper)`
  flex: 1;
  background: ${(props) => props.theme.palette.background.default};

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`;

const Dashboard = ({ children }) => {
  const router = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [kbPages, setKbPages] = useState([]);
  const [kbLoading, setKbLoading] = useState(false);
  const isKBRoute = router.pathname.startsWith("/knowledge-base");

  // LWI-720 — collapsed/expanded sidebar (desktop only). Persisted in
  // localStorage so the choice survives reloads. Default: expanded.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  });
  const handleSidebarToggleCollapse = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? "1" : "0");
      } catch {
        // localStorage may be unavailable (private mode); state still toggles.
      }
      return next;
    });
  };
  const desktopDrawerWidth = sidebarCollapsed ? drawerWidthCollapsed : drawerWidth;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Close mobile menu when navigation occurs
  useEffect(() => {
    setMobileOpen(false);
  }, [router.pathname]);

  const refreshKBPages = () => {
    if (kbPages.length === 0) setKbLoading(true);
    apiClient.get("/knowledge-base/pages")
      .then(r => setKbPages(r.data))
      .catch((err) => {
        console.error("Failed to load KB pages:", err);
      })
      .finally(() => setKbLoading(false));
  };

  useEffect(() => {
    if (!isKBRoute) return;
    refreshKBPages();
  }, [router.pathname]);

  const theme = useTheme();

  return (
    <Root>
      <CssBaseline />
      <GlobalStyle />
      <Drawer $width={desktopDrawerWidth}>
        <Box sx={{ display: { xs: "block", lg: "none" } }}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            items={dashboardItems}
          />
        </Box>
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <Sidebar
            PaperProps={{ style: { width: desktopDrawerWidth, transition: "width 0.2s" } }}
            items={dashboardItems}
            collapsed={sidebarCollapsed}
            onToggleCollapse={handleSidebarToggleCollapse}
          />
        </Box>
      </Drawer>
      {isKBRoute && (
        <Suspense fallback={null}>
          <KBPanel pages={kbPages} loading={kbLoading} onRefresh={refreshKBPages} />
        </Suspense>
      )}
      <AppContent $kbOpen={isKBRoute} $drawerWidth={desktopDrawerWidth}>
        <ImpersonationBanner />
        <StalePermissionsBanner />
        <Navbar onDrawerToggle={handleDrawerToggle} />
        {/* 40px (5 × 8px) above and below the page; the bottom gap keeps the
            last content off the footer (LWI-1674). */}
        <MainContent pt={5} pb={5}>
          <Container maxWidth="xxl">
            {children}
            <Outlet />
          </Container>
        </MainContent>
        <Footer />
      </AppContent>
    </Root>
  );
};

export default Dashboard;
